import { Component } from '@theme/component';

/**
 * Links the numbered markers on the Shop the look image to their pieces
 * (sections/kp-shop-the-look.liquid).
 *
 * Two states: `pinned` (set by a tap or click, stays until tapped again,
 * Escape, or a click elsewhere) and `previewed` (pointer over a marker or a
 * list row, mouse only). Whatever is previewed wins while the pointer is
 * there; otherwise the pinned piece shows. In the "image" layout the shown
 * piece's card opens on the photo; in the "list" layout its row lights up.
 */
class KpShopTheLook extends Component {
  #abortController = new AbortController();
  #pinned = 0;
  #previewed = 0;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    document.addEventListener('click', (event) => {
      if (this.#pinned && !this.contains(/** @type {Node} */ (event.target))) this.#pin(0);
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.#pinned) return;
      const marker = this.#marker(this.#pinned);
      this.#pin(0);
      marker?.focus();
    }, { signal });

    // Markers ring once when the image first comes into view
    const observer = new IntersectionObserver(([entry], obs) => {
      if (!entry?.isIntersecting) return;
      this.dataset.seen = 'true';
      obs.disconnect();
    }, { threshold: 0.4 });
    observer.observe(this);
    signal.addEventListener('abort', () => observer.disconnect());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /* ---------- Declarative handlers ---------- */

  /** @param {number} index - 1-based marker number */
  select(index) {
    index = Number(index);
    this.#pin(this.#pinned === index ? 0 : index);

    // On phones the list is a strip under the photo: bring the row into view
    if (this.#pinned && this.dataset.layout === 'list') {
      this.#item(index)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }

  /**
   * @param {number} index
   * @param {Event} [event]
   */
  preview(index, event) {
    // Touch fires pointerenter right before the tap; let select() handle it
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;
    this.#previewed = Number(index);
    this.#render();
  }

  unpreview() {
    this.#previewed = 0;
    this.#render();
  }

  /* ---------- State ---------- */

  #pin(/** @type {number} */ index) {
    this.#pinned = index;
    this.#render();
  }

  #render() {
    const shown = this.#previewed || this.#pinned;
    const showCards = this.dataset.layout === 'image';

    for (const hotspot of this.#list('hotspots')) {
      const index = Number(hotspot.dataset.index);
      const active = index === shown;
      hotspot.toggleAttribute('data-active', active);

      const card = hotspot.querySelector('.kp-look-hotspot__card');
      if (card instanceof HTMLElement) card.hidden = !(showCards && active);

      hotspot.querySelector('.kp-look-hotspot__marker')?.setAttribute('aria-expanded', String(showCards && active));
    }

    for (const item of this.#list('items')) {
      item.toggleAttribute('data-active', Number(item.dataset.index) === shown);
    }
  }

  /** @param {'hotspots' | 'items'} name */
  #list(name) {
    const value = this.refs[name];
    if (!value) return [];
    return /** @type {HTMLElement[]} */ (Array.isArray(value) ? value : [value]);
  }

  #marker(/** @type {number} */ index) {
    const hotspot = this.#list('hotspots').find((el) => Number(el.dataset.index) === index);
    return /** @type {HTMLElement | null | undefined} */ (hotspot?.querySelector('.kp-look-hotspot__marker'));
  }

  #item(/** @type {number} */ index) {
    return this.#list('items').find((el) => Number(el.dataset.index) === index);
  }
}

if (!customElements.get('kp-shop-the-look')) {
  customElements.define('kp-shop-the-look', KpShopTheLook);
}
